import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Types ──────────────────────────────────────────────────────────────────

export interface DriverDto {
  id: string;
  fullName: string;
  phone: string;
  licenseNumber?: string;
  defaultVehicleReg?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  /** Linked user account ID for mobile trip tracking. */
  userId?: string;
  linkedUserName?: string;
  mobileAccountLinked?: boolean;
  mobileAccountActive?: boolean;
  mobileAssignmentReadiness?: "Ready" | "Missing" | "Inactive";
}

export interface CreateDriverRequest {
  fullName: string;
  phone: string;
  licenseNumber?: string;
  defaultVehicleReg?: string;
  notes?: string;
}

export interface UpdateDriverRequest {
  fullName: string;
  phone: string;
  licenseNumber?: string;
  defaultVehicleReg?: string;
  notes?: string;
  isActive: boolean;
}

// ── API ────────────────────────────────────────────────────────────────────

export const driversApi = {
  getAll: async (activeOnly = true): Promise<DriverDto[]> => {
    const res = await apiClient.get<ApiResponse<DriverDto[]>>(`/drivers`, {
      params: { activeOnly },
    });
    return res.data.data ?? [];
  },

  getById: async (id: string): Promise<DriverDto> => {
    const res = await apiClient.get<ApiResponse<DriverDto>>(`/drivers/${id}`);
    return res.data.data;
  },

  create: async (data: CreateDriverRequest): Promise<DriverDto> => {
    const res = await apiClient.post<ApiResponse<DriverDto>>(`/drivers`, data);
    return res.data.data;
  },

  update: async (id: string, data: UpdateDriverRequest): Promise<DriverDto> => {
    const res = await apiClient.put<ApiResponse<DriverDto>>(`/drivers/${id}`, data);
    return res.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/drivers/${id}`);
  },

  linkUser: async (id: string, userId: string | null): Promise<DriverDto> => {
    const res = await apiClient.patch<ApiResponse<DriverDto>>(`/drivers/${id}/link-user`, { userId });
    return res.data.data;
  },

  promoteToUser: async (id: string, email: string): Promise<DriverDto> => {
    const res = await apiClient.post<ApiResponse<DriverDto>>(`/drivers/${id}/promote-to-user`, { email });
    return res.data.data;
  },
};
